import { CalibrationVideo } from '@/types';
import React, { useEffect, useState } from 'react';
import { Badge, Button, Col, Modal, Row, Spinner } from 'react-bootstrap';
import { useError } from "../../contexts/ErrorContext";
import { useSuccess } from "../../contexts/SuccessContext";
import { CalibrationVideosAPI } from '../../services/api';

interface CalibrationVideoDisplayProps {
    calibrationVideo: CalibrationVideo,
    isActiveCalibration: boolean,
    hide: () => void,
    onDelete?: () => void
}

const CalibrationVideoDisplay: React.FC<CalibrationVideoDisplayProps> = ({
    calibrationVideo, isActiveCalibration, hide, onDelete
}) => {
    const [refreshedCalibrationVideo, setRefreshedCalibrationVideo] = useState<CalibrationVideo | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const { setError } = useError();
    const { setSuccess } = useSuccess();

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await CalibrationVideosAPI.deleteVideo(calibrationVideo.id);
            setSuccess('Calibration video deleted successfully');
            setShowDeleteConfirm(false);
            hide();
            if (onDelete) {
                onDelete();
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.error || 'Failed to delete calibration video';
            setError(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    useEffect(() => {
        // A cleanup flag to prevent state updates on an unmounted component
        let isMounted = true;

        // Guard clause: do nothing if there's no video object
        if (!calibrationVideo) {
            return;
        }

        // Reset state when the video prop changes
        setRefreshedCalibrationVideo(null);
        setError(null);


        // Otherwise, fetch the refreshed URL
        setIsLoading(true);
        CalibrationVideosAPI.refreshUrl(calibrationVideo.id)
            .then((response) => {
                if (isMounted) {
                    setRefreshedCalibrationVideo(response.data);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setError('Failed to load the video. Please try again.');
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });


        // Cleanup function: runs when the component unmounts or dependencies change
        return () => {
            isMounted = false;
        };
    }, [calibrationVideo, setError]); // Effect dependencies

    const renderVideo = () => {
        if (isLoading) {
            return (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            );
        }

        if (refreshedCalibrationVideo?.url) {
            return (
                <video controls autoPlay className="w-100 mt-2">
                    <source src={refreshedCalibrationVideo.url} type={refreshedCalibrationVideo.content_type} />
                    Your browser does not support the video tag.
                </video>
            );
        }

        return null; // Should not happen in normal flow, but a good fallback
    };

    // A more robust check to see if there's any calibration data to display
    const hasCalibrationData = (calibrationVideo.intrinsics && calibrationVideo.intrinsics.length > 0) ||
        (calibrationVideo.distortion_coeffs && calibrationVideo.distortion_coeffs.length > 0);
    const hasCalibrationError = calibrationVideo.detailed_error && calibrationVideo.detailed_error.length > 0

    return (
        <Modal show={calibrationVideo !== null} onHide={hide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Calibration Video Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col>
                        <div>
                            <strong>{calibrationVideo.title}</strong>
                            {isActiveCalibration && (
                                <Badge bg="success" className="ms-2">Active</Badge>
                            )}
                            <Badge
                                bg={
                                    calibrationVideo.status === 'completed' ? 'success' :
                                        calibrationVideo.status === 'error' ? 'danger' :
                                            calibrationVideo.status === 'canceled' ? 'secondary' :
                                                'warning'
                                }
                                className="ms-2"
                            >
                                {calibrationVideo.status.charAt(0).toUpperCase() + calibrationVideo.status.slice(1)}
                            </Badge>
                            {calibrationVideo.calibrator_type && (
                                <Badge bg="info" className="ms-2">
                                    {calibrationVideo.calibrator_type === 'advanced' ? 'Advanced' : 'Basic'}
                                </Badge>
                            )}
                        </div>
                        <div>
                            <small>Type: {calibrationVideo.type}</small>
                            {calibrationVideo.date_time && (
                                <small className="ms-2">Date: {new Date(calibrationVideo.date_time).toLocaleString()}</small>
                            )}
                        </div>
                        {(calibrationVideo.camera_model || calibrationVideo.camera_mode) && (
                            <div className="mt-1">
                                <small>
                                    <strong>Camera:</strong>{' '}
                                    {calibrationVideo.camera_model || 'Unknown'}
                                    {calibrationVideo.camera_mode && ` - ${calibrationVideo.camera_mode}`}
                                </small>
                            </div>
                        )}
                        {renderVideo()}
                        <Button
                            variant="primary"
                            as="a"
                            className="mt-2"
                            // Disable the button if there's no video URL (e.g., during load or error)
                            disabled={!refreshedCalibrationVideo?.url}
                            href={refreshedCalibrationVideo?.url}
                            target="_blank"
                            download
                        >
                            Download
                        </Button>
                    </Col>
                    <Col>
                        {/* Calibration Data Display */}
                        {hasCalibrationData ? (
                            <div className="mt-3 mt-lg-0">
                                <h6>Calibration Data</h6>

                                {calibrationVideo.image_width && calibrationVideo.image_height && (
                                    <div className="mb-3 p-2 bg-light border rounded">
                                        <strong>Image Dimensions:</strong> {calibrationVideo.image_width} × {calibrationVideo.image_height} pixels
                                        <br />
                                        <small className="text-muted">
                                            Note: Calibration parameters are pixel-based and must be scaled proportionally when resizing images.
                                        </small>
                                    </div>
                                )}

                                {calibrationVideo.intrinsics && calibrationVideo.intrinsics.length === 9 && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>Camera Intrinsics Matrix (3x3)</strong>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                    const matrix = [
                                                        [calibrationVideo.intrinsics![0], calibrationVideo.intrinsics![1], calibrationVideo.intrinsics![2]],
                                                        [calibrationVideo.intrinsics![3], calibrationVideo.intrinsics![4], calibrationVideo.intrinsics![5]],
                                                        [calibrationVideo.intrinsics![6], calibrationVideo.intrinsics![7], calibrationVideo.intrinsics![8]]
                                                    ];
                                                    const text = matrix.map(row =>
                                                        '[' + row.map(val => val.toFixed(6)).join(', ') + ']'
                                                    ).join('\n');
                                                    navigator.clipboard.writeText(text);
                                                    setSuccess('Intrinsics matrix copied to clipboard');
                                                }}
                                                title="Copy intrinsics matrix"
                                            >
                                                📋
                                            </Button>
                                        </div>
                                        <pre style={{
                                            fontSize: '0.85rem',
                                            margin: 0,
                                            backgroundColor: '#f8f9fa',
                                            padding: '10px',
                                            borderRadius: '4px',
                                            fontFamily: 'monospace',
                                            border: '1px solid #dee2e6'
                                        }}>
                                            {(() => {
                                                const matrix = [
                                                    [calibrationVideo.intrinsics[0], calibrationVideo.intrinsics[1], calibrationVideo.intrinsics[2]],
                                                    [calibrationVideo.intrinsics[3], calibrationVideo.intrinsics[4], calibrationVideo.intrinsics[5]],
                                                    [calibrationVideo.intrinsics[6], calibrationVideo.intrinsics[7], calibrationVideo.intrinsics[8]]
                                                ];
                                                return matrix.map(row =>
                                                    '[' + row.map(val => val.toFixed(6)).join(', ') + ']'
                                                ).join('\n');
                                            })()}
                                        </pre>
                                        <small className="text-muted">Format: [fx, 0, cx; 0, fy, cy; 0, 0, 1]</small>
                                    </div>
                                )}

                                {calibrationVideo.distortion_coeffs && calibrationVideo.distortion_coeffs.length > 0 && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>Distortion Coefficients</strong>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                    const text = '[' + calibrationVideo.distortion_coeffs!.map(val => val.toFixed(6)).join(', ') + ']';
                                                    navigator.clipboard.writeText(text);
                                                    setSuccess('Distortion coefficients copied to clipboard');
                                                }}
                                                title="Copy distortion coefficients"
                                            >
                                                📋
                                            </Button>
                                        </div>
                                        <pre style={{
                                            fontSize: '0.85rem',
                                            margin: 0,
                                            backgroundColor: '#f8f9fa',
                                            padding: '10px',
                                            borderRadius: '4px',
                                            fontFamily: 'monospace',
                                            border: '1px solid #dee2e6'
                                        }}>
                                            {'[' + calibrationVideo.distortion_coeffs.map(val => val.toFixed(6)).join(', ') + ']'}
                                        </pre>
                                        <small className="text-muted">
                                            {calibrationVideo.distortion_coeffs.length === 4 && 'Format: [k1, k2, p1, p2] (4 coefficients)'}
                                            {calibrationVideo.distortion_coeffs.length === 5 && 'Format: [k1, k2, p1, p2, k3] (5 coefficients)'}
                                            {calibrationVideo.distortion_coeffs.length === 8 && 'Format: [k1, k2, p1, p2, k3, k4, k5, k6] (8 coefficients)'}
                                            {![4, 5, 8].includes(calibrationVideo.distortion_coeffs.length) && `${calibrationVideo.distortion_coeffs.length} coefficients`}
                                        </small>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="mt-3 mt-lg-0 text-muted">
                                No calibration succeeded yet.
                            </div>
                        )}
                        {hasCalibrationError && (
                            <div className="mt-3 mt-lg-0">
                                <h6>Calibration Error</h6>
                                <pre style={{
                                    fontSize: '0.85rem',
                                    margin: 0,
                                    backgroundColor: '#f8f9fa',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    fontFamily: 'monospace',
                                    border: '1px solid #dee2e6'
                                }}>
                                    {calibrationVideo.detailed_error}
                                </pre>
                            </div>
                        )}
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="danger"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isActiveCalibration}
                    title={isActiveCalibration ? "Cannot delete active calibration video" : "Delete this video"}
                >
                    Delete
                </Button>
                <Button variant="secondary" onClick={hide}>
                    Close
                </Button>
            </Modal.Footer>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this calibration video?</p>
                    <p><strong>{calibrationVideo.title}</strong></p>
                    {isActiveCalibration && (
                        <div className="alert alert-warning">
                            This video is currently set as the active calibration for this camera.
                            Please set a different calibration video first before deleting.
                        </div>
                    )}
                    <p className="text-muted">This action cannot be undone. The video will be permanently deleted from both the database and blob storage.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={isDeleting || isActiveCalibration}>
                        {isDeleting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Modal>
    );
};

export default CalibrationVideoDisplay;
